import { graphHooks } from '@tunasong/graph-lib/react'
import type { PollOption } from '@tunasong/schemas'
import { EntityLink } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { OptionAudio } from './option-audio.js'
import { hasOwnLabel } from './option-label.js'
import css from './poll-option-name.module.css'

export interface PollOptionNameProps {
  option: Pick<PollOption, 'name' | 'entityRef' | 'audio'>
  /**
   * Include the option's play controls. Pass false inside a <label> (vote, radio and checkbox rows) and render
   * them beside it with WithOptionAudio instead: a click inside a label can activate its control.
   */
  withAudio?: boolean
}

/**
 * The one place an option's display is decided. The human label is the option; a linked entity
 * is the secondary reference beneath it, still one click away. With no label the entity link is
 * all there is, exactly as before labels existed. The entity itself is never renamed.
 *
 * An option's chosen audio is played from here too, so every poll view gets the controls.
 */
export const PollOptionName: FC<PollOptionNameProps> = props => {
  const { option, withAudio = true } = props
  const { entity } = graphHooks.useEntity(option.entityRef)
  const audio = withAudio ? <OptionAudio option={option} /> : null

  if (!(option.entityRef && entity)) {
    return (
      <>
        {option.name}
        {audio}
      </>
    )
  }

  if (!hasOwnLabel(option, entity.name)) {
    return (
      <>
        <EntityLink entity={entity} />
        {audio}
      </>
    )
  }

  return (
    <>
      <span className={css.root}>
        <span className={css.label}>{option.name}</span>
        <span className={css.reference}>
          <EntityLink entity={entity} />
        </span>
      </span>
      {audio}
    </>
  )
}

export default PollOptionName
